/**
 * Modular Firebase initialisation. Replaces the firebase/compat shim.
 *
 * Two named apps coexist:
 *   - default → `electronvisualized` (web account realm)
 *   - "atomizer" → `atomizer-ar` (mobile account realm; same project
 *     used by AtomizerAR iOS and Electronify React Native)
 *
 * Other modules import `webAuth`, `mobileAuth`, and the helpers from
 * here instead of pulling firebase/compat (which would re-bundle the
 * full SDK). Every app-level Firebase call routes through this file.
 */
import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAuth, Auth } from "firebase/auth";

const webConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: "electronvisualized.firebaseapp.com",
  projectId: "electronvisualized",
  storageBucket: "electronvisualized.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const mobileConfig = {
  apiKey:
    process.env.REACT_APP_FIREBASE_MOBILE_API_KEY ||
    "AIzaSyB8pMkgkzjDN7k2VacBvqwxadGUVE5owgA",
  authDomain: "atomizer-ar.firebaseapp.com",
  projectId: "atomizer-ar",
  storageBucket: "atomizer-ar.firebasestorage.app",
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MOBILE_MESSAGING_SENDER_ID || "472294695443",
  appId:
    process.env.REACT_APP_FIREBASE_MOBILE_APP_ID ||
    "1:472294695443:web:b167aee2fa9a37e40c55e6",
  measurementId:
    process.env.REACT_APP_FIREBASE_MOBILE_MEASUREMENT_ID || "G-HSV53FD6ZR",
};

const MOBILE_APP_NAME = "atomizer";

function getOrInit(name: string | undefined, config: any): FirebaseApp {
  const apps = getApps();
  const existing = name
    ? apps.find((a) => a.name === name)
    : apps.find((a) => a.name === "[DEFAULT]");
  if (existing) return existing;
  return name ? initializeApp(config, name) : initializeApp(config);
}

export const firebaseApp: FirebaseApp = getOrInit(undefined, webConfig);
export const mobileApp: FirebaseApp = getOrInit(MOBILE_APP_NAME, mobileConfig);

export const webAuth: Auth = getAuth(firebaseApp);
export const mobileAuth: Auth = getAuth(mobileApp);

/** The Firebase user from whichever realm holds an active session. */
export function currentUser() {
  return webAuth.currentUser || mobileAuth.currentUser;
}
